import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Phone, MessageCircle, Eye } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../AdminLayout";
import { adminAxios, formatErr } from "../api";

const STATUS_MAP = {
  pending: { bg: "#FEF3C7", color: "#F59E0B", label: "Pending" },
  approved: { bg: "#DCFCE7", color: "#22C55E", label: "Approved" },
  rejected: { bg: "#FEE2E2", color: "#EF4444", label: "Rejected" },
};

const Pill = ({ status }) => {
  const m = STATUS_MAP[status] || STATUS_MAP.pending;
  return <span className="text-xs font-semibold px-2 py-1 rounded-full" style={{ backgroundColor: m.bg, color: m.color }}>{m.label}</span>;
};

export default function AdminApplications() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const activeStatus = params.get("status") || "";

  const load = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (activeStatus) qs.set("status", activeStatus);
      qs.set("limit", String(pageSize));
      qs.set("skip", String((page - 1) * pageSize));
      const { data } = await adminAxios.get(`/applications?${qs.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeStatus, page, pageSize]);

  const setFilter = (s) => {
    const next = new URLSearchParams(params);
    if (s) next.set("status", s); else next.delete("status");
    setParams(next);
  };

  return (
    <AdminLayout title="Technician Applications">
      <div className="flex gap-2 mb-4" data-testid="application-status-tabs">
        {[
          { id: "", label: "All" },
          { id: "pending", label: "Pending" },
          { id: "approved", label: "Approved" },
          { id: "rejected", label: "Rejected" },
        ].map((t) => (
          <button
            key={t.id || "all"}
            data-testid={`app-tab-${t.id || "all"}`}
            onClick={() => setFilter(t.id)}
            className={`px-4 py-2 rounded-full text-xs font-semibold transition-all ${
              activeStatus === t.id ? "bg-[#1A3C6E] text-white" : "bg-white border border-slate-200 text-slate-700 hover:border-[#1A3C6E]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 text-sm text-slate-500">
          {loading ? "Loading…" : `Showing ${Math.min((page-1)*pageSize+1, total)} - ${Math.min(page*pageSize, total)} of ${total} applications`}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="applications-table">
            <thead className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
              <tr>
                <th className="px-5 py-2.5">Ref</th>
                <th className="px-5 py-2.5">Name</th>
                <th className="px-5 py-2.5">Phone</th>
                <th className="px-5 py-2.5">City</th>
                <th className="px-5 py-2.5">Experience</th>
                <th className="px-5 py-2.5">Skills</th>
                <th className="px-5 py-2.5">Date</th>
                <th className="px-5 py-2.5">Status</th>
                <th className="px-5 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading && items.length === 0 && (
                <tr><td colSpan={9} className="px-5 py-10 text-center text-slate-400">No applications.</td></tr>
              )}
              {items.map((a) => (
                <tr key={a.ref_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`app-row-${a.ref_id}`}>
                  <td className="px-5 py-3 font-mono text-xs text-[#1A3C6E]">
                    <Link to={`/admin/applications/${a.ref_id}`} className="hover:underline font-semibold">{a.ref_id}</Link>
                  </td>
                  <td className="px-5 py-3 font-medium">{a.full_name}</td>
                  <td className="px-5 py-3">{a.phone}</td>
                  <td className="px-5 py-3 capitalize">{a.city}</td>
                  <td className="px-5 py-3">{a.experience}</td>
                  <td className="px-5 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(a.services || []).map((s) => (
                        <span key={s} className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 capitalize">{s.replace(/_/g, " ")}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-xs text-slate-500">{new Date(a.created_at).toLocaleDateString("en-IN")}</td>
                  <td className="px-5 py-3"><Pill status={a.status} /></td>
                  <td className="px-5 py-3">
                    <div className="flex justify-end gap-1">
                      <Link to={`/admin/applications/${a.ref_id}`} className="p-1.5 rounded-md hover:bg-slate-200" title="View" data-testid={`app-view-${a.ref_id}`}>
                        <Eye className="w-4 h-4 text-slate-600" />
                      </Link>
                      <a href={`tel:${a.phone}`} className="p-1.5 rounded-md hover:bg-slate-200" title="Call"><Phone className="w-4 h-4 text-slate-600" /></a>
                      <a href={`https://wa.me/91${a.phone}`} target="_blank" rel="noreferrer" className="p-1.5 rounded-md hover:bg-green-100" title="WhatsApp"><MessageCircle className="w-4 h-4 text-[#22C55E]" /></a>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="flex items-center justify-between mt-3">
        <div className="flex items-center gap-2">
          <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p-1))} className="px-3 py-1 rounded-md border">Previous</button>
          <button disabled={(page*pageSize) >= total} onClick={() => setPage((p) => p+1)} className="px-3 py-1 rounded-md border">Next</button>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-slate-500">Per page</label>
          <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} className="px-2 py-1 border rounded">
            {[10,25,50,100].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      </div>
    </AdminLayout>
  );
}
